import type {Builder} from '@wix/document-services-types'
import {SKIP_STYLE_TYPE, STYLE_TYPE_MAP} from '../constants/style'
import type {SerializedVariable} from '../types'
import {convertDMVariableToCSSValue} from '../dataMappers'

export interface ProcessedStyleVariable {
    name: string
    styleItem: Builder.StyleItem
}

export interface IVariableStyleTransformer {
    transformVariablesToStyles(
        variables: SerializedVariable[],
        defaultValues: Record<string, unknown>
    ): Record<string, Builder.StyleItem>
}

export class VariableStyleTransformer implements IVariableStyleTransformer {
    transformVariablesToStyles(
        variables: SerializedVariable[],
        defaultValues: Record<string, unknown>
    ): Record<string, Builder.StyleItem> {
        const processedVariables = variables
            .filter(variable => variable.name && defaultValues[variable.name])
            .map(variable => this.transformVariableToStyle(variable, defaultValues[variable.name]))
            .filter((item): item is ProcessedStyleVariable => item !== null)

        return Object.fromEntries(processedVariables.map(({name, styleItem}) => [name, styleItem]))
    }

    private transformVariableToStyle(
        variable: SerializedVariable,
        defaultValue: unknown
    ): ProcessedStyleVariable | null {
        if (this.shouldSkipVariable(variable, defaultValue)) {
            return null
        }

        const styleItem = STYLE_TYPE_MAP[variable.type]
        const cssValue = convertDMVariableToCSSValue(styleItem, defaultValue)

        return {
            name: variable.name,
            styleItem: {
                ...styleItem,
                defaultValue: cssValue,
                displayName: variable.name,
                cssCustomProperty: true
            }
        }
    }

    private shouldSkipVariable(variable: SerializedVariable, defaultValue: unknown): boolean {
        if (!variable.name || !defaultValue) return true

        const styleItem = STYLE_TYPE_MAP[variable.type]
        const isSkippedType = SKIP_STYLE_TYPE[variable.type]

        return !styleItem || isSkippedType
    }
}
